import { groupLifeService } from '@/services';
import type { GroupLifeMembersPageResponse } from '@/services/group-life';
import { useQueries } from '@tanstack/react-query';

/**
 * Mesma key do `useMembersPage` (wizard de cotação): o CRUD de vidas invalida
 * pelo prefixo `['group-life', 'members-page', policyId, targetId]`, e assim
 * contagens e páginas daqui também são atualizadas.
 */
export function membersPageKey(
  policyId: string,
  targetId: string,
  pageNo: number,
  pageSize: number
) {
  return [
    'group-life',
    'members-page',
    policyId,
    targetId,
    pageNo,
    pageSize,
    '',
  ] as const;
}

export async function fetchMembersPage(
  policyId: string,
  targetId: string,
  pageNo: number,
  pageSize: number
): Promise<GroupLifeMembersPageResponse> {
  const res = await groupLifeService.listMembersPage({
    quoteId: policyId,
    targetId,
    pageNo,
    pageSize,
  });
  if (!res.success || !res.data) {
    throw new Error(res.error ?? 'Falha ao carregar as vidas');
  }
  return res.data;
}

export type GroupLifeLivesCount = {
  /** Total de cada target, na ordem pedida. */
  targetTotals: { targetId: string; total: number }[];
  /** Total de cada target, por id. */
  totalByTarget: Record<string, number>;
  /** Soma de todos os targets. */
  total: number;
  /** Todos os totais já chegaram. */
  isReady: boolean;
  isFetching: boolean;
};

/**
 * Quantidade de vidas de cada target (estipulante master e subestipulantes),
 * contada pelo backend: uma página de 1 vida do `/members/page` por target.
 * Não baixa nenhuma lista.
 */
export function useGroupLifeLivesCount(params: {
  policyId: string | undefined;
  targetIds: string[];
}): GroupLifeLivesCount {
  const { policyId, targetIds } = params;

  const queries = useQueries({
    queries: targetIds.map(targetId => ({
      queryKey: membersPageKey(policyId ?? '', targetId, 1, 1),
      queryFn: () => fetchMembersPage(policyId ?? '', targetId, 1, 1),
      enabled: !!policyId,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    })),
  });

  const targetTotals = targetIds.map((targetId, index) => ({
    targetId,
    total: queries[index]?.data?.total ?? 0,
  }));

  return {
    targetTotals,
    totalByTarget: Object.fromEntries(
      targetTotals.map(({ targetId, total }) => [targetId, total])
    ),
    total: targetTotals.reduce((acc, target) => acc + target.total, 0),
    isReady: !!policyId && queries.every(query => query.data != null),
    isFetching: queries.some(query => query.isFetching),
  };
}
